// components/FundraiserSection.js
import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs, doc, deleteDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import EditFundraiserForm from "./EditFundraiserForm";
import { getStorage, ref, getDownloadURL } from "firebase/storage";


import { FiEdit2, FiTrash2, FiPlus, FiCheck, FiX, FiEye } from "react-icons/fi";


export default function FundraiserSection({ schoolId, clubId }) {
    const [fundraisers, setFundraisers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [adding, setAdding] = useState(false);


    const updateDonorStatus = async (schoolId, fundraiserId, donorId, status) => {
        const donorRef = doc(db, "schools", schoolId, "fundraisers", fundraiserId, "donors", donorId);
        try {
            await updateDoc(donorRef, { confirmed: status });
            alert(`Status updated to ${status}`);

            // Refresh data
            const updatedDonors = await fetchDonors(fundraiserId);
            setFundraisers((prev) =>
                prev.map((f) =>
                    f.id === fundraiserId ? { ...f, donors: updatedDonors } : f
                )
            );
        } catch (error) {
            console.error("Error updating status:", error);
            alert("Failed to update status.");
        }
    };


    const toggleFundraiserStatus = async (fundraiserId, newStatus) => {
        try {
            const ref = doc(db, "schools", schoolId, "fundraisers", fundraiserId);
            await updateDoc(ref, { is_active: newStatus });

            // Refresh state
            const updated = fundraisers.map(f =>
                f.id === fundraiserId ? { ...f, is_active: newStatus } : f
            );
            setFundraisers(updated);
        } catch (error) {
            console.error("Failed to toggle fundraiser status:", error);
        }
    };


    useEffect(() => {
        const fetchFundraisers = async () => {
            const ref = collection(db, "schools", schoolId, "fundraisers");
            const q = query(ref, where("club_id", "==", clubId));
            const snap = await getDocs(q);
            const list = await Promise.all(snap.docs.map(async (doc) => {
                const data = doc.data();
                const donors = await fetchDonors(doc.id);
                const fullData = { id: doc.id, ...data, donors };
                console.log("Fetched fundraiser with donors:", fullData);
                return fullData;
            }));


            setFundraisers(list);
            setLoading(false);
        };



        fetchFundraisers();
    }, [schoolId, clubId]);

    const fetchDonors = async (fundraiserId) => {
        const donorRef = collection(db, "schools", schoolId, "fundraisers", fundraiserId, "donors");
        const snap = await getDocs(donorRef);
        const donors = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log(`Donors for fundraiser ${fundraiserId}:`, donors);
        return donors;
    };



    const handleDelete = async (id) => {
        const confirm = window.confirm("Are you sure you want to delete this fundraiser?");
        if (!confirm) return;
        await deleteDoc(doc(db, "schools", schoolId, "fundraisers", id));
        setFundraisers((prev) => prev.filter((f) => f.id !== id));
    };

    const handleSave = async (updated) => {
        try {
            const fundraiserRef = doc(db, "schools", schoolId, "fundraisers", updated.id);

            console.log("Updating fundraiser:", updated);

            await updateDoc(fundraiserRef, {
                club_id: updated.club_id,
                donations: updated.donations,
            });

            const updatedList = fundraisers.map((f) => (f.id === updated.id ? updated : f));
            setFundraisers(updatedList);
            setEditingId(null);
        } catch (error) {
            console.error("Error saving fundraiser:", error);
            alert("Failed to save fundraiser.");
        }
    };



    const handleAdd = (newItem) => {
        setFundraisers((prev) => [newItem, ...prev]);
        setAdding(false);
    };



    const handleViewReceipt = async (path) => {
        try {
            const storage = getStorage();
            const fileRef = ref(storage, path);
            const url = await getDownloadURL(fileRef);
            window.open(url, "_blank");
        } catch (error) {
            console.error("Failed to fetch receipt:", error);
            alert("Unable to view receipt. It may not exist or you may not have permission.");
        }
    };


    if (loading) return <p>Loading fundraisers...</p>;




    return (
        <div className="fundraiser-section">
            <h3>Fundraisers</h3>

            {(editingId !== null || adding) ? (
                <EditFundraiserForm
                    schoolId={schoolId}
                    clubId={clubId}
                    data={editingId !== null ? fundraisers.find((f) => f.id === editingId) : null}
                    onCancel={() => {
                        setEditingId(null);
                        setAdding(false);
                    }}
                    onSave={editingId !== null ? handleSave : handleAdd}
                />
            ) : (
                <>
                    <button onClick={() => setAdding(true)} className="add-btn">
                        <FiPlus /> Add Fundraiser
                    </button>

                    <div className="fundraiser-list">
                        {fundraisers.map((item) =>
                            Object.values(item.donations || {}).map((donation, index) => (
                                <div key={`${item.id}-${index}`} className="fundraiser-card" style={{ border: "1px solid #ccc", padding: 16, marginBottom: 20, borderRadius: 8 }}>
                                    <p><strong>Status:</strong> {item.is_active ? "✅ Active" : "🚫 Inactive"}</p>

                                    <h4 style={{ marginBottom: 6 }}>{donation.title}</h4>
                                    <p><strong>Event:</strong> {donation.event_name || "-"}</p>
                                    <p><strong>Date:</strong> {donation.event_date?.seconds
                                        ? new Date(donation.event_date.seconds * 1000).toLocaleDateString("ms-MY", {
                                            day: "2-digit",
                                            month: "long",
                                            year: "numeric"
                                        })
                                        : donation.event_date || "-"}
                                    </p>
                                    <p><strong>Location:</strong> {donation.location || "-"}</p>
                                    <p><strong>Goal Amount:</strong> RM{donation.goal_amount || "-"}</p>
                                    <p><strong>Note:</strong> {donation.note || "-"}</p>

                                    {donation.image && (
                                        <img
                                            src={donation.image}
                                            alt="Fundraiser Poster"
                                            style={{ width: 300, borderRadius: 8, marginTop: 10 }}
                                        />
                                    )}

                                    {donation.permission_url && (
                                        <p style={{ marginTop: 10 }}>
                                            <a href={donation.permission_url} target="_blank" rel="noopener noreferrer">
                                                📄 View Permission Letter
                                            </a>
                                        </p>
                                    )}


                                    <div className="fundraiser-actions" >
                                        <button onClick={() => setEditingId(item.id)}>Edit</button>
                                        <button
                                            onClick={() => handleDelete(item.id)}

                                        >
                                            Delete
                                        </button>
                                        <button
                                            onClick={() => toggleFundraiserStatus(item.id, !item.is_active)}
                                            style={{ marginLeft: 10 }}
                                        >
                                            {item.is_active ? "Deactivate" : "Activate"}
                                        </button>
                                    </div>

                                    {/* Donor Table */}
                                    {item.donors && item.donors.length > 0 && (
                                        <>
                                            <h5 style={{ marginTop: 20 }}>Donors</h5>
                                            <div className="donor-table-wrapper">
                                                <table className="donor-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Name</th>
                                                            <th>Amount</th>
                                                            <th>Receipt</th>
                                                            <th>Status</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {item.donors.map((donor) => (
                                                            <tr key={donor.id}>
                                                                <td>
                                                                    {donor.date?.seconds
                                                                        ? new Date(donor.date.seconds * 1000).toLocaleString("ms-MY", {
                                                                            day: "2-digit",
                                                                            month: "2-digit",
                                                                            year: "2-digit",
                                                                            hour: "2-digit",
                                                                            minute: "2-digit",
                                                                            hour12: false,
                                                                        })
                                                                        : "—"}
                                                                </td>
                                                                <td>{donor.show_name ? donor.name : "Anonymous"}</td>
                                                                <td>RM{donor.amount}</td>
                                                                <td>
                                                                    {donor.receipt_filename ? (
                                                                        <button
                                                                            onClick={() => handleViewReceipt(donor.receipt_filename)}
                                                                            style={{ backgroundColor: "lightgray", color: "#000", width: "50px" }}
                                                                            title="View Receipt"
                                                                        >
                                                                            <FiEye />
                                                                        </button>


                                                                    ) : "–"}
                                                                </td>
                                                                <td>
                                                                    {donor.confirmed === "confirmed"
                                                                        ? "✅"
                                                                        : donor.confirmed === "invalid"
                                                                            ? "❌"
                                                                            : "⏳"}
                                                                </td>
                                                                <td>
                                                                    <div >
                                                                        <button
                                                                            style={{ width: "50px" }}
                                                                            onClick={() =>
                                                                                updateDonorStatus(schoolId, item.id, donor.id, "confirmed")
                                                                            }>
                                                                            <FiCheck />
                                                                        </button>
                                                                        <button
                                                                            style={{ width: "50px" }}

                                                                            onClick={() =>
                                                                                updateDonorStatus(schoolId, item.id, donor.id, "invalid")
                                                                            }
                                                                            className="btn-danger"
                                                                        >
                                                                            <FiX />
                                                                        </button>
                                                                    </div>
                                                                </td>

                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>


                                        </>
                                    )}
                                </div>
                            ))
                        )}
                    </div>


                </>
            )}
        </div>
    );

}
